import { onBundleProductFragment } from '@pylot-data/hooks/product/graphql/Fragments/onBundleProductFragment'
import { AssociatedProductsFragment } from '@pylot-data/hooks/product/graphql/Fragments/AssociatedProductsFragment'

/**
 * EVO-263: `swatch_image_data` se pide también para el producto del tile, no sólo para sus
 * hermanos. La fila de colores lo incluye —para poder volver al color con el que se entró a la
 * PLP—, y sin este campo su miniatura caería al `small_image`, que es la foto ambientada, y se
 * vería distinta de las demás.
 *
 * La explicación va acá y no dentro del template literal a propósito: los documentos GraphQL de
 * este repositorio no llevan comentarios `#`. `graphqlFetch` los limpia antes de minificar, pero
 * el minificador del edge no lo hacía y un `#` truncaba la query completa (incidente del
 * 2026-09-12). No vale la pena dejar el precedente.
 */
export const productsFragment = /* GraphQL */ `
aggregations {
    __typename
    attribute_code
    count
    label
    options {
        __typename
        label
        value
        count
        swatch_data {
            value
            type
        }
    }
}
__typename
sort_fields {
    __typename
    default
    options {
        __typename
        label
        value
    }
}
items {
    __typename
    id
    name
    stock_status
    ppum
    is_set
    price_range {
        __typename
        minimum_price {
            __typename
            regular_price {
                __typename
                value
                currency
            }
            final_price {
                __typename
                value
                currency
            }
            discount {
                __typename
                amount_off
            }
        }
        maximum_price {
            __typename
            final_price {
                __typename
                value
                currency
            }
            regular_price {
                __typename
                value
                currency
            }
        }
    }
    sku
    badge_image_text
    badge_caption_icon
    new_from_date
    new_to_date
    media_gallery {
        __typename
        url
        disabled
        label
        position
    }
    small_image {
        __typename
        url
    }
    swatch_image_data {
        __typename
        url
        label
    }
    url_key
    categories {
        name
        url_path
        breadcrumbs {
            category_id
            category_name
        }
    }
    flag {
        type
        label
        from
        to
    }
    rating_summary
    review_count
    ${AssociatedProductsFragment}
    ${onBundleProductFragment}
    ... on ConfigurableProduct {
        configurable_options {
            __typename
            attribute_code
            attribute_id
            id
            label
            values {
                __typename
                value_index
                label
                swatch_data {
                    __typename
                    value
                    ... on ImageSwatchData {
                        thumbnail
                        __typename
                    }
                }
            }
        }
        variants {
            __typename
            attributes {
                __typename
                code
                value_index
                label
                uid
            }
            product {
                __typename
                id
                small_image {
                    __typename
                    url
                }
                sku
                stock_status
                price_range {
                    __typename
                    minimum_price {
                        __typename
                        regular_price {
                            __typename
                            value
                            currency
                        }
                        final_price {
                            __typename
                            value
                            currency
                        }
                        discount {
                            __typename
                            amount_off
                        }
                    }
                    maximum_price {
                        __typename
                        final_price {
                            __typename
                            value
                            currency
                        }
                        regular_price {
                            __typename
                            value
                            currency
                        }
                    }
                }
            }
        }
        __typename
    }
    ... on GiftCardProduct {
        allow_open_amount
        open_amount_min
        open_amount_max
        giftcard_type
        is_redeemable
        lifetime
        allow_message
        message_max_length
        gift_card_options {
            ... on CustomizableFieldOption {
                title
                required
                value {
                    uid
                }
            }
        }
        giftcard_amounts {
            uid
            website_id
            website_value
            attribute_id
            value
        }
    }
}
page_info {
    __typename
    total_pages
    current_page
}
total_count
search_request_id
`

export const productsFragment2 = /* GraphQL */ `
__typename
aggregations {
    attribute_code
    label
    options {
        label
        value
        count
    }
}
sort_fields {
    default
    options {
        label
        value
        __typename
    }
}
items {
    __typename
    name
    sku
    canonical_url
    price_range {
        __typename
        minimum_price {
            __typename
            regular_price {
                __typename
                value
                currency
            }
            final_price {
                __typename
                value
                currency
            }
            discount {
                __typename
                amount_off
            }
        }
        maximum_price {
            __typename
            regular_price {
                __typename
                value
                currency
            }
            final_price {
                __typename
                value
                currency
            }
            discount {
                __typename
                amount_off
            }
        }
    }
    small_image {
        __typename
        url
    }
}
page_info {
    __typename
    total_pages
    current_page
}
total_count
search_request_id
visual_search_result_mode
`
